import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { map, Observable, startWith, take } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { DatasetService } from 'src/app/services/dataset.service';
import { DynamicTableService } from 'src/app/services/dynamic-table.service';
import { Dataset, DatasetType, FILE_STORAGE_REFERENCE_ID, DataSchemaDataType } from 'src/app/services/models/datasets.models';
import { FileBrowserModalComponent, FileBrowserModalData } from 'src/app/common/bhub-common-module/file-browser-modal/file-browser-modal.component';
import { DynamicTableFilters, MatchMode, FilterCriteria, AggregationCriteria, AggregationFunction, ROW_COUNT_ALIAS, aggregationAlias } from 'src/app/services/models/dynamic-table.models';
import { FileManagementService } from 'src/app/services/file-management.service';
import { AppState } from 'src/app/services/models/application-details.model';
import { selectCurrentUserEmail } from 'src/app/store/app-state.selectors';
import * as XLSX from 'xlsx';

@Component({
  selector: 'bhub-data-entry',
  templateUrl: './data-entry.component.html',
  styleUrl: './data-entry.component.scss',
  standalone: false,
  encapsulation: ViewEncapsulation.None
})
export class DataEntryComponent implements OnInit {
  // Expose Object to template
  Object = Object;

  datasetControl = new FormControl();
  filteredDatasets: Observable<Dataset[]>;
  datasets: Dataset[] = [];
  selectedDataset: Dataset | null = null;
  referencePicklist: { [key: string]: any[] } = {};  // To store picklist values for each column

  // Table data
  data: any[] = []; // Existing data
  newRows: any[] = []; // New rows for adding
  columns: any[] = [];
  pageSize = 50;
  totalEntries = 0;

  // Add Entries control
  isEditing: boolean = false;

  get isViewDataset(): boolean {
    return this.selectedDataset?.datasetReferenceTable?.datasetType === DatasetType.View;
  }

  // Pagination
  first = 0;

  // Lazy loading mode (enabled for large datasets)
  isLazyLoadingEnabled = false;
  readonly LAZY_LOADING_THRESHOLD = 1000; // Switch to lazy loading for datasets with more than 1000 rows
  readonly REFERENCE_PICKLIST_LIMIT = 200; // Limit reference picklists to 200 options to prevent performance issues
  isLoadingData = false;

  // Store mapping between display values and reference values
  referenceDisplayMapping: { [fieldName: string]: { [displayValue: string]: any } } = {};

  // Primary key validation
  primaryKeyFields: string[] = [];
  validationErrors: { [key: string]: string } = {};

  // File upload properties
  selectedFile: File | null = null;
  selectedGCSFilePath: string | null = null;
  isImporting = false;
  csvDelimiter: string = ','; // Default to comma
  // Note: For tab, we store '\t' as a string, but convert it when parsing

  // Column selection - which columns the server is asked for. Every column starts selected.
  selectedColumnFields: string[] = [];

  // Group By configuration. groupByFields/aggregationRows are the draft the user edits;
  // activeGroupBy/activeAggregations are what the currently displayed data was loaded with.
  groupByFields: string[] = [];
  aggregationRows: { fieldName: string, func: AggregationFunction }[] = [];
  isGroupedView = false;
  groupedColumns: any[] = [];
  private activeGroupBy: number[] = [];
  private activeAggregations: AggregationCriteria[] = [];
  // Dataset the column/group state belongs to, so a plain reload doesn't discard it
  private columnStateDatasetId: number | null = null;
  // Column selection the data was last loaded with, to ignore no-op picker closes
  private lastAppliedColumnSelection = '';

  // Sentinel for "count the rows in each group" rather than the values of one column
  readonly ROW_COUNT_FIELD = '__row_count__';

  readonly aggregationFunctions: { value: AggregationFunction, label: string, numericOnly: boolean }[] = [
    { value: 'sum', label: 'Sum', numericOnly: true },
    { value: 'avg', label: 'Average', numericOnly: true },
    { value: 'min', label: 'Minimum', numericOnly: false },
    { value: 'max', label: 'Maximum', numericOnly: false },
    { value: 'count', label: 'Count', numericOnly: false },
    { value: 'count_distinct', label: 'Count (distinct)', numericOnly: false }
  ];

  // Export properties
  exportFormat: string = 'csv';
  isExporting = false;
  // Subfolder of the user's own storage that exports land in, so My Files doesn't
  // collect one loose file per export.
  private readonly EXPORT_FOLDER = 'Exports';

  // Filter properties
  filterRows: {
    fieldId: number | null,
    fieldName: string,
    value: string,
    value2?: string,  // For 'between' match mode
    dateValue?: Date | null,  // For DateTime fields
    dateValue2?: Date | null,  // For DateTime fields with 'between' mode
    matchMode: MatchMode,
    dataType: string,
    availableMatchModes?: { label: string, value: MatchMode }[]  // Cache available modes
  }[] = [];
  activeFilters: DynamicTableFilters = {};
  hasActiveFilters = false;

  constructor(
    private datasetService: DatasetService,
    private dynamicTableService: DynamicTableService,
    private dialog: MatDialog,
    private fileService: FileManagementService,
    private store: Store<AppState>
  ) {}

  ngOnInit(): void {
    // Fetch dataset list
    this.datasetService.getDatasetReferences().subscribe((datasetMap) => {
      this.datasets = Array.from(datasetMap.values());
      this.filteredDatasets = this.datasetControl.valueChanges.pipe(
        startWith(''),
        map(value => this.filterDatasets(value))
      );
    });
  }

  private filterDatasets(value: string): Dataset[] {
    const filterValue = value.toLowerCase();

    // If the value matches the selected dataset name, show all datasets
    if (this.selectedDataset && this.selectedDataset.datasetReferenceTable.datasetName.toLowerCase() === filterValue) {
      return this.datasets; // Show all datasets after selection
    }

    return this.datasets.filter(dataset => dataset.datasetReferenceTable.datasetName.toLowerCase().includes(filterValue));
  }

  // This is triggered when a user selects a dataset from the dropdown
  onDatasetSelect(event: any): void {
    const datasetName = event.option.value;
    this.selectedDataset = this.datasets.find(d => d.datasetReferenceTable.datasetName === datasetName) || null;
    if (this.selectedDataset) {
      // Clear any existing filters when switching datasets
      this.resetFiltersState();
      // Fetch and display the raw data for the selected dataset
      this.loadDatasetData();
      this.isEditing = false; // Reset the editing state
      // Keep the selected dataset in the input field, but show all datasets in the dropdown
      this.datasetControl.setValue(this.selectedDataset.datasetReferenceTable.datasetName);
    }
  }

  loadDatasetData(): void {
    if (!this.selectedDataset) return;

    // Identify primary key fields
    this.primaryKeyFields = this.selectedDataset.dataTableSchemas
      .filter(schema => schema.isPrimaryKey)
      .map(schema => schema.fieldName);

    // Step 1: Convert the datatableSchemas into columns for PrimeNG table
    // Sort by ordinal field first, then map to columns
    this.columns = this.selectedDataset.dataTableSchemas
      .sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0))
      .map((schema) => {
        return {
          field: schema.fieldName, // Property name in the rowData
          header: schema.displayName || schema.fieldName, // Column header name
          fieldId: schema.fieldId, // Needed to ask the server for a projection, group-by or aggregate
          sortable: true,
          referenceId: schema.referenceDataId, // Include the referenceId
          dataType: schema.dataType, // Include the data type for conditional rendering
          isPrimaryKey: schema.isPrimaryKey, // Track primary key status
          isHiddenInternal: schema.fieldName === 'Hidden_Internal_PrimaryKey', // Track hidden internal key
          isFileStorageReference: schema.isFileStorageReference || schema.referenceDataId === FILE_STORAGE_REFERENCE_ID // Track file storage reference
        };
      });

    // Step 2: Column selection and grouping are expressed in terms of this dataset's
    // columns, so start them over for a new dataset - but leave them alone when this is
    // a reload of the same dataset (after a save or an import), which shouldn't quietly
    // throw away what the user set up.
    const datasetId = this.selectedDataset.datasetReferenceTable.datasetId;
    if (this.columnStateDatasetId !== datasetId) {
      this.resetColumnAndGroupState();
      this.columnStateDatasetId = datasetId;
    }

    // Step 3: Check dataset size to determine loading strategy
    this.refreshData();
  }

  /**
   * Loads the data for the current dataset, filters, column selection and grouping.
   * Counts first, then either pages through the results or loads them in one go.
   */
  private refreshData(): void {
    if (!this.selectedDataset) return;

    this.isLoadingData = true;
    this.dynamicTableService.getDatasetCount(
      this.selectedDataset.datasetReferenceTable.datasetId,
      this.hasActiveFilters ? this.activeFilters : undefined,
      this.isGroupedView ? this.activeGroupBy : undefined
    ).subscribe(
      (count) => {
        this.totalEntries = count;

        // Decide whether to use lazy loading based on the size of the result
        if (count > this.LAZY_LOADING_THRESHOLD) {
          // Large result: Enable lazy loading and load first page only
          console.log(`Result has ${count} rows. Enabling lazy loading mode.`);
          this.isLazyLoadingEnabled = true;
          this.first = 0; // Reset to first page
          this.loadPage(0, this.pageSize);
        } else {
          // Small result: Load all data at once (existing behavior)
          console.log(`Result has ${count} rows. Loading all data at once.`);
          this.isLazyLoadingEnabled = false;
          this.loadAllData();
        }
      },
      (error) => {
        console.error('Error getting row count, falling back to loading all data:', error);
        // Fallback: If count fails, try to load all data (existing behavior)
        this.isLazyLoadingEnabled = false;
        this.loadAllData();
      }
    );
  }

  /**
   * Load all data at once (for small results)
   */
  private loadAllData(): void {
    this.loadRows(-1, undefined);
  }

  /**
   * Load a specific page of data (for large results with lazy loading)
   */
  private loadPage(offset: number, limit: number): void {
    this.isLoadingData = true;
    this.loadRows(limit, offset);
  }

  /**
   * Single request path for the data table: carries whatever combination of filters,
   * column selection and grouping is currently applied.
   */
  private loadRows(limit: number, offset?: number): void {
    if (!this.selectedDataset) return;

    this.dynamicTableService.selectEntries(
      this.selectedDataset.datasetReferenceTable.datasetId,
      limit,
      offset,
      this.hasActiveFilters ? this.activeFilters : undefined,
      undefined,
      // Paging a grouped query needs a deterministic order, or the same group can show up
      // on two pages while another never does
      this.isGroupedView ? this.activeGroupBy.map(fieldId => ({ fieldId, direction: 'asc' as const })) : undefined,
      this.isGroupedView ? undefined : this.getRequestedFieldIds(),
      this.isGroupedView ? this.activeGroupBy : undefined,
      this.isGroupedView ? this.activeAggregations : undefined
    ).subscribe(
      (response) => {
        this.totalEntries = response.totalCount || response.rowData.length;
        var stringifiedRowData = this.stringifyRowData(response.rowData);
        this.data = stringifiedRowData;

        // Aggregated rows hold no reference values or editable dates - the columns they
        // are grouped into are built from the request instead.
        if (!this.isGroupedView) {
          // Load picklist values for reference columns (only on first page load)
          if (!offset) {
            this.loadReferencePicklists();
          }

          // Initialize DateTime fields for existing data
          this.initializeDateTimeFields();
        }

        this.isLoadingData = false;
      },
      (error) => {
        console.error('Error loading data:', error);
        this.isLoadingData = false;
      }
    );
  }

  //For each entry in the rowData, convert the values to string
  stringifyRowData(rowData: any[]): any[] {
    return rowData.map(row => {
      var stringifiedRow = {};
      for (var key in row) {
        stringifiedRow[key] = row[key] === null ? '' : row[key].toString();
      }
      return stringifiedRow;
    });
  }

  //For row data linked to a reference hierarchy, pull the potential values for a picklist
  loadReferencePicklists(): void {
    // Loop through all columns and fetch picklist for those with referenceId
    this.columns.forEach(col => {
      // Skip file storage fields (referenceId = -1)
      if (col.referenceId && col.referenceId !== 0 && col.referenceId !== FILE_STORAGE_REFERENCE_ID) {
        // Get the schema for the current field to check for fieldToDisplay
        const currentFieldSchema = this.selectedDataset?.dataTableSchemas.find(
          schema => schema.fieldName === col.field
        );

        if (currentFieldSchema?.fieldToDisplay) {
          // Use the new approach with display field
          this.dynamicTableService.getDisplayValuesForReference(
            col.referenceId,
            currentFieldSchema.referenceFieldId ? this.getReferenceFieldName(col.referenceId, currentFieldSchema.referenceFieldId) : '',
            currentFieldSchema.fieldToDisplay,
            this.REFERENCE_PICKLIST_LIMIT
          ).subscribe((response) => {
            // Get the primary key field name for the referenced dataset
            const primaryKeyFieldName = this.getPrimaryKeyField(col.referenceId);

            // Create dropdown options with display value shown but reference value stored
            const options = response.rowData.map(item => {
              const rawValue = item[primaryKeyFieldName] || item[Object.keys(item)[0]];
              // Ensure consistent data type - convert to string to match how data is typically stored
              const value = rawValue?.toString() || rawValue;
              return {
                label: item[currentFieldSchema.fieldToDisplay] || '',
                value: value
              };
            });

            this.referencePicklist[col.field] = options;

            // Warn if the picklist was limited
            if (response.totalCount && response.totalCount > this.REFERENCE_PICKLIST_LIMIT) {
              console.warn(`Reference field "${col.field}" has ${response.totalCount} options. Showing only first ${this.REFERENCE_PICKLIST_LIMIT} for performance. Consider implementing search/autocomplete for better user experience.`);
            }

            // Create mapping for reverse lookup (display value -> reference value)
            this.referenceDisplayMapping[col.field] = {};
            options.forEach(option => {
              this.referenceDisplayMapping[col.field][option.label] = option.value;
            });

            // Check if any current data values don't have matching options
            const currentValues = this.data.map(row => row[col.field]).filter(val => val);
            const unmatchedValues = currentValues.filter(val => !options.find(opt => opt.value == val));
            if (unmatchedValues.length > 0) {
              console.warn(`Unmatched values in ${col.field}:`, unmatchedValues, 'Available options:', options.map(o => o.value));
            }
          });
        } else {
          // Fall back to the original approach if no fieldToDisplay is set
          this.dynamicTableService.getValuesForDatasetPrimaryKey(col.referenceId)
            .subscribe((picklist) => {
              this.referencePicklist[col.field] = picklist.values.map(value => ({
                label: value,
                value: value
              }));
            });
        }
      }
    });
  }

  onReferenceValueChange(row: any, column: any): void {
    // Handle the change event for a referenced value
    this.validatePrimaryKeys(); // Revalidate when values change
  }

  onFieldValueChange(row: any, column: any): void {
    // Handle field value changes
    this.validatePrimaryKeys(); // Revalidate when values change
  }

  private getReferenceFieldName(datasetId: number, fieldId: number): string {
    // Get the reference dataset to find the field name by fieldId
    const referenceDataset = this.datasets.find(d => d.datasetReferenceTable.datasetId === datasetId);
    const field = referenceDataset?.dataTableSchemas.find(schema => schema.fieldId === fieldId);
    return field?.fieldName || '';
  }

  private getPrimaryKeyField(datasetId: number): string {
    // Get the reference dataset to find the primary key field
    const referenceDataset = this.datasets.find(d => d.datasetReferenceTable.datasetId === datasetId);
    const primaryKeyField = referenceDataset?.dataTableSchemas.find(schema => schema.isPrimaryKey);
    return primaryKeyField?.fieldName || referenceDataset?.datasetReferenceTable.primaryKey || '';
  }

  getDisplayValueForReference(fieldName: string, value: any): string {
    if (!this.referencePicklist[fieldName] || !value) {
      return value || '';
    }

    // Find the option with matching value and return its label
    // Use loose equality (==) to handle string vs number comparison
    const option = this.referencePicklist[fieldName].find((opt: any) => opt.value == value);
    return option ? option.label : value;
  }

  // Primary key validation methods
  validatePrimaryKeys(): boolean {
    this.validationErrors = {};
    let isValid = true;

    // Get all primary key values from existing data and new rows
    const allRows = [...this.data, ...this.newRows];
    const primaryKeyValueCounts: { [key: string]: number } = {};

    allRows.forEach((row, index) => {
      // Build primary key string from all primary key fields
      const primaryKeyValue = this.primaryKeyFields
        .map(field => row[field] || '')
        .join('|');

      // Skip empty primary keys (for new rows that haven't been filled yet)
      if (primaryKeyValue.replace(/\|/g, '').trim() === '') {
        return;
      }

      // Count occurrences
      primaryKeyValueCounts[primaryKeyValue] = (primaryKeyValueCounts[primaryKeyValue] || 0) + 1;

      // Mark duplicates
      if (primaryKeyValueCounts[primaryKeyValue] > 1) {
        const errorKey = `row_${index}`;
        this.validationErrors[errorKey] = `Duplicate primary key: ${primaryKeyValue}`;
        isValid = false;
      }
    });

    return isValid;
  }

  isFieldEditable(column: any, isExistingRow: boolean = false): boolean {
    // Hidden_Internal_PrimaryKey should not be editable
    if (column.isHiddenInternal) {
      return false;
    }

    // Primary keys should not be editable when updating existing rows
    if (isExistingRow && column.isPrimaryKey) {
      return false;
    }

    return true;
  }

  getRowValidationError(rowIndex: number, isNewRow: boolean): string | null {
    const errorKey = isNewRow ? `new_row_${rowIndex}` : `row_${rowIndex}`;
    return this.validationErrors[errorKey] || null;
  }

  sortColumn(event: any): void {
    const column = event.field;
    const sortOrder = event.order === 1 ? 'asc' : 'desc';

    this.data.sort((a, b) => {
      if (a[column] < b[column]) {
        return sortOrder === 'asc' ? -1 : 1;
      }
      if (a[column] > b[column]) {
        return sortOrder === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  onPageChange(event: any): void {
    this.pageSize = event.rows;
    this.first = event.first;

    // If lazy loading is enabled, load the new page from server
    if (this.isLazyLoadingEnabled) {
      // PrimeNG provides the offset directly. loadPage carries whatever filters,
      // column selection and grouping are applied.
      this.loadPage(event.first, event.rows);
    }
    // For small datasets, all data is already loaded, so just update the display
  }

  toggleEdit(): void {
    // View datasets are read-only, and so is a grouped result - its rows are aggregates,
    // not rows of the table
    if (!this.canEditData) return;
    this.isEditing = !this.isEditing;

    if (this.isEditing) {
      // Initialize with one empty row for adding new entries
      this.addNewRow();
    } else {
      // Clear new rows when exiting edit mode
      this.newRows = [];
    }
  }

  addNewRow(): void {
    // Create a new empty row for the add section
    const newEntry = {};
    this.columns.forEach(col => {
      if (col.isHiddenInternal) {
        // Hidden_Internal_PrimaryKey will be auto-generated, leave empty
        newEntry[col.field] = '';
      } else {
        newEntry[col.field] = ''; // Initialize each field with an empty string
      }
    });

    this.newRows.push(newEntry);

    // Initialize DateTime fields for the new row
    this.columns.forEach(column => {
      if (this.isDateTimeField(column)) {
        this.initializeDateTimeField(newEntry, column);
      }
    });
  }

  removeNewRow(index: number): void {
    // Remove new row (not yet saved to backend) - simple splice is sufficient
    if (this.newRows.length > 1) { // Always keep at least one row
      this.newRows.splice(index, 1);
      this.validatePrimaryKeys(); // Revalidate after removal
    }
  }

  deleteExistingRow(index: number): void {
    // Delete existing row (already saved to backend) - requires API call
    if (confirm('Are you sure you want to delete this row?')) {
      const rowToDelete = this.data[index];

      // Call the backend to delete the row
      this.dynamicTableService.deleteEntries(this.selectedDataset.datasetReferenceTable.datasetId, [rowToDelete])
        .subscribe(success => {
          if (success) {
            // Remove from local data only after successful backend deletion
            this.data.splice(index, 1);
            this.validatePrimaryKeys(); // Revalidate after removal
            alert('Row deleted successfully!');
          } else {
            alert('Error deleting row. Please try again.');
          }
        });
    }
  }

  submitNewEntries() {
    // Validate primary keys first
    if (!this.validatePrimaryKeys()) {
      alert('Please fix primary key conflicts before submitting.');
      return;
    }

    // Filter out empty rows (rows where all fields are empty)
    const nonEmptyRows = this.newRows.filter(row =>
      Object.values(row).some(value => value && value.toString().trim() !== '')
    );

    if (nonEmptyRows.length === 0) {
      alert('Please enter some data before submitting.');
      return;
    }

    // Process the new rows to ensure reference fields are correct
    const processedData = this.processRowDataForSubmission(nonEmptyRows);

    this.dynamicTableService.addEntries(this.selectedDataset.datasetReferenceTable.datasetId, processedData)
        .subscribe(success => {
          if (success) {
            alert(`Successfully added ${processedData.length} new entries!`);
            this.newRows = []; // Clear the new rows
            this.addNewRow(); // Add one empty row for next additions
            this.loadDatasetData(); // Refresh the data to show updated values
          } else {
            alert('Error adding data.');
          }
        });
  }

  submitRowUpdate(row: any, rowIndex: number) {
    // Process a single row for submission
    const processedData = this.processRowDataForSubmission([{ ...row }]);

    this.dynamicTableService.updateEntries(this.selectedDataset.datasetReferenceTable.datasetId, processedData)
        .subscribe(success => {
          if (success) {
            alert('Row updated successfully!');
            this.loadDatasetData(); // Refresh the data to show updated values
          } else {
            alert('Error updating row.');
          }
        });
  }

  private processRowDataForSubmission(rows: any[]): any[] {
    return rows.map(row => {
      const newRow = { ...row };

      this.columns.forEach(col => {
        if (col.referenceId && col.referenceId !== 0) {
          // The dropdown should already have the correct value from optionValue="value"
          // But let's ensure no display values accidentally got stored
          const currentFieldSchema = this.selectedDataset?.dataTableSchemas.find(
            schema => schema.fieldName === col.field
          );

          if (currentFieldSchema?.fieldToDisplay && this.referenceDisplayMapping[col.field]) {
            // Check if the value is actually a display value and convert if needed
            const displayValue = newRow[col.field];
            const referenceValue = this.referenceDisplayMapping[col.field][displayValue];
            if (referenceValue !== undefined) {
              newRow[col.field] = referenceValue;
            }
          }
        }
      });

      return newRow;
    });
  }


  onInputFocus(event: FocusEvent): void {
    // You can add any custom behavior when the input field gains focus
    event.target['style'].borderColor = '#007ad9'; // For example, change border color
  }

  onInputBlur(event: FocusEvent): void {
    // Handle input blur event
  }

  isDateTimeField(column: any): boolean {
    return column.dataType === DataSchemaDataType.DateTime;
  }

  isBooleanField(column: any): boolean {
    return column.dataType === DataSchemaDataType.Boolean;
  }

  isDateTimeFilterRow(filterRow: any): boolean {
    return filterRow.dataType === DataSchemaDataType.DateTime;
  }

  isFileStorageField(column: any): boolean {
    return column.referenceId === FILE_STORAGE_REFERENCE_ID;
  }

  openFileBrowser(row: any, column: any): void {
    const dialogData: FileBrowserModalData = {
      title: `Select File for ${column.header}`,
      allowedStorageAreas: ['user', 'organization', 'admin']
    };

    const dialogRef = this.dialog.open(FileBrowserModalComponent, {
      width: '700px',
      maxHeight: '80vh',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(selectedPath => {
      if (selectedPath) {
        row[column.field] = selectedPath;
        this.validatePrimaryKeys(); // Revalidate in case this was a primary key field
      }
    });
  }

  // Time selection properties for DateTime fields
  getTimeOptions(field: string, row: any) {
    if (!row[`${field}_timeOptions`]) {
      row[`${field}_timeOptions`] = {
        hour: 12,
        minute: 0,
        period: 'AM'
      };
    }
    return row[`${field}_timeOptions`];
  }

  getHourOptions(): any[] {
    return Array.from({length: 12}, (_, i) => ({ label: (i + 1).toString(), value: i + 1 }));
  }

  getMinuteOptions(): any[] {
    return Array.from({length: 60}, (_, i) => ({ label: i.toString().padStart(2, '0'), value: i }));
  }

  getPeriodOptions(): any[] {
    return [{ label: 'AM', value: 'AM' }, { label: 'PM', value: 'PM' }];
  }

  onDateChange(event: MatDatepickerInputEvent<Date>, row: any, column: any): void {
    if (event.value) {
      // Store the date temporarily
      row[`${column.field}_date`] = event.value;
      this.updateDateTimeField(row, column);
    } else {
      row[column.field] = '';
      row[`${column.field}_date`] = null;
    }
  }

  onTimeChange(row: any, column: any): void {
    this.updateDateTimeField(row, column);
  }

  updateDateTimeField(row: any, column: any): void {
    const date = row[`${column.field}_date`];
    if (!date) {
      row[column.field] = '';
      return;
    }

    const timeOptions = this.getTimeOptions(column.field, row);
    const newDate = new Date(date);

    // Convert 12-hour format to 24-hour format
    let hour = timeOptions.hour;
    if (timeOptions.period === 'PM' && hour !== 12) {
      hour += 12;
    } else if (timeOptions.period === 'AM' && hour === 12) {
      hour = 0;
    }

    newDate.setHours(hour, timeOptions.minute, 0, 0);

    // Create a local ISO string that preserves the local time
    const year = newDate.getFullYear();
    const month = String(newDate.getMonth() + 1).padStart(2, '0');
    const day = String(newDate.getDate()).padStart(2, '0');
    const hours = String(newDate.getHours()).padStart(2, '0');
    const minutes = String(newDate.getMinutes()).padStart(2, '0');
    const seconds = String(newDate.getSeconds()).padStart(2, '0');

    // Store as local time without timezone conversion
    row[column.field] = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }

  formatDateDisplay(value: string): string {
    if (!value) return '';
    try {
      const date = new Date(value);
      return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
    } catch {
      return '';
    }
  }

  initializeDateTimeField(row: any, column: any): void {
    if (row[column.field]) {
      try {
        const date = new Date(row[column.field]);
        row[`${column.field}_date`] = date;

        let hour = date.getHours();
        let period = 'AM';

        if (hour >= 12) {
          period = 'PM';
          if (hour > 12) hour -= 12;
        }
        if (hour === 0) hour = 12;

        row[`${column.field}_timeOptions`] = {
          hour: hour,
          minute: date.getMinutes(),
          period: period
        };
      } catch {
        // Invalid date, initialize with defaults
        this.getTimeOptions(column.field, row);
      }
    } else {
      this.getTimeOptions(column.field, row);
    }
  }

  initializeDateTimeFields(): void {
    // Initialize DateTime fields for all data rows
    this.data.forEach(row => {
      this.columns.forEach(column => {
        if (this.isDateTimeField(column)) {
          this.initializeDateTimeField(row, column);
        }
      });
    });

    // Initialize DateTime fields for new rows
    this.newRows.forEach(row => {
      this.columns.forEach(column => {
        if (this.isDateTimeField(column)) {
          this.initializeDateTimeField(row, column);
        }
      });
    });
  }

  // File upload methods
  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.selectedFile = file;
      this.selectedGCSFilePath = null; // Clear GCS selection when local file is selected
    }
  }

  openGCSFileBrowser(): void {
    const dialogData: FileBrowserModalData = {
      title: 'Select File from GCS',
      allowedStorageAreas: ['user', 'organization', 'admin']
    };

    const dialogRef = this.dialog.open(FileBrowserModalComponent, {
      width: '700px',
      maxHeight: '80vh',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(selectedPath => {
      if (selectedPath) {
        this.selectedGCSFilePath = selectedPath;
        this.selectedFile = null; // Clear local file selection when GCS file is selected
      }
    });
  }

  async importFile(): Promise<void> {
    if (!this.selectedDataset) return;

    // Check if importing from GCS or local file
    if (this.selectedGCSFilePath) {
      await this.importFromGCS();
    } else if (this.selectedFile) {
      await this.importFromLocalFile();
    } else {
      alert('Please select a file to import.');
      return;
    }
  }

  private async importFromGCS(): Promise<void> {
    if (!this.selectedGCSFilePath || !this.selectedDataset) return;

    this.isImporting = true;

    try {
      // Determine if file is CSV to pass delimiter
      const isCSV = this.selectedGCSFilePath.toLowerCase().endsWith('.csv');
      const delimiter = isCSV ? this.csvDelimiter : undefined;

      this.dynamicTableService.importFromGCS(
        this.selectedDataset.datasetReferenceTable.datasetId,
        this.selectedGCSFilePath,
        delimiter
      ).subscribe({
        next: (success) => {
          if (success) {
            // Refresh the data after import
            this.loadDatasetData();
            // Clear the selected file
            this.selectedGCSFilePath = null;
            alert('Data imported successfully from GCS!');
          } else {
            alert('Error importing file from GCS. Please try again.');
          }
          this.isImporting = false;
        },
        error: (error) => {
          console.error('Error importing from GCS:', error);
          alert('Error importing file from GCS. Please check the file and try again.');
          this.isImporting = false;
        }
      });
    } catch (error) {
      console.error('Error importing from GCS:', error);
      alert('Error importing file from GCS. Please try again.');
      this.isImporting = false;
    }
  }

  exportToGCS(): void {
    const dataset = this.selectedDataset;
    if (!dataset) return;

    // The export endpoint writes to whatever bucket path it is given, so point it at
    // the signed-in user's own storage area. Anything else lands outside the areas the
    // Files tab lists, leaving the export unreachable from the app.
    this.store.pipe(select(selectCurrentUserEmail), take(1)).subscribe(userEmail => {
      if (!userEmail) {
        alert('Could not determine your user folder. Please refresh the page and try again.');
        return;
      }

      const datasetName = dataset.datasetReferenceTable.datasetName;
      // Date and time (UTC), so two exports of the same dataset on one day - different
      // filters, say - don't land on the same name and overwrite each other
      const timestamp = new Date().toISOString().slice(0, 19).replace('T', '_').replace(/:/g, '');
      const suffix = this.isGroupedView ? '_grouped' : '';
      const fileName = `${this.sanitizeForFileName(datasetName)}${suffix}_${timestamp}.${this.exportFormat}`;
      const filePath = this.fileService.getUserStoragePath(userEmail, `${this.EXPORT_FOLDER}/${fileName}`);
      const applicationId = dataset.datasetReferenceTable.applicationId;
      const datasetId = dataset.datasetReferenceTable.datasetId;

      this.isExporting = true;
      // Export what is on screen: the same filters, columns and grouping the table was
      // loaded with, so the file matches what the user is looking at
      this.dynamicTableService.exportToGCS(
        applicationId,
        datasetId,
        filePath,
        this.exportFormat,
        undefined,
        this.hasActiveFilters ? this.activeFilters : undefined,
        this.isGroupedView ? undefined : this.getRequestedFieldIds(),
        this.isGroupedView ? this.activeGroupBy : undefined,
        this.isGroupedView ? this.activeAggregations : undefined
      ).subscribe({
        next: (response) => {
          alert(`Export successful!\n\nSaved to: My Files / ${this.EXPORT_FOLDER} / ${fileName}\nFormat: ${response.fileFormat}\nContents: ${this.describeExportScope()}\nRows exported: ${response.rowCount}`);
          this.isExporting = false;
        },
        error: (error) => {
          console.error('Error exporting to GCS:', error);
          alert('Error exporting data. Please try again.');
          this.isExporting = false;
        }
      });
    });
  }

  // Keep the dataset name usable as a single file name - a slash in it would
  // silently nest the export in another folder.
  private sanitizeForFileName(name: string): string {
    return name.replace(/[^a-zA-Z0-9-_]/g, '_');
  }

  /**
   * Spells out what an export covers, so a filtered or grouped file isn't mistaken for
   * the whole dataset later.
   */
  private describeExportScope(): string {
    const parts: string[] = [];

    if (this.isGroupedView) {
      parts.push(`grouped by ${this.groupByFields.length} column(s)`);
    } else if (this.selectedColumnFields.length < this.getSelectableColumns().length) {
      parts.push(`${this.selectedColumnFields.length} of ${this.getSelectableColumns().length} columns`);
    }

    if (this.hasActiveFilters) {
      parts.push(`${Object.keys(this.activeFilters).length} filter(s) applied`);
    }

    return parts.length > 0 ? parts.join(', ') : 'all rows and columns';
  }

  private async importFromLocalFile(): Promise<void> {
    if (!this.selectedFile || !this.selectedDataset) return;

    this.isImporting = true;

    try {
      const data = await this.parseFile(this.selectedFile);
      await this.processImportedData(data);

      // Refresh the data after import
      this.loadDatasetData();

      // Clear the selected file
      this.selectedFile = null;

      // Reset file input
      const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }

      alert('Data imported successfully!');
    } catch (error) {
      console.error('Error importing file:', error);
      alert('Error importing file. Please check the file format and try again.');
    } finally {
      this.isImporting = false;
    }
  }

  private async parseFile(file: File): Promise<any[]> {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    switch (fileExtension) {
      case 'csv':
        return await this.parseCSV(file);
      case 'xlsx':
      case 'xls':
        return await this.parseExcel(file);
      default:
        throw new Error(`Unsupported file type: ${fileExtension}`);
    }
  }

  private async parseCSV(file: File): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const csv = e.target?.result as string;
          const lines = csv.split(/\r?\n/).filter(line => line.trim());

          if (lines.length < 2) {
            reject(new Error('CSV file must have a header row and at least one data row.'));
            return;
          }

          // Parse CSV line handling quoted values with configurable delimiter
          const parseCSVLine = (line: string, delimiter: string): string[] => {
            const result: string[] = [];
            let current = '';
            let inQuotes = false;

            for (let i = 0; i < line.length; i++) {
              const char = line[i];
              const nextChar = line[i + 1];

              if (char === '"') {
                if (inQuotes && nextChar === '"') {
                  // Escaped quote
                  current += '"';
                  i++; // Skip next quote
                } else {
                  // Toggle quote state
                  inQuotes = !inQuotes;
                }
              } else if (char === delimiter && !inQuotes) {
                // End of field
                result.push(current.trim());
                current = '';
              } else {
                current += char;
              }
            }
            // Add last field
            result.push(current.trim());
            return result;
          };

          const delimiter = this.csvDelimiter === '\\t' ? '\t' : this.csvDelimiter;
          const headers = parseCSVLine(lines[0], delimiter).map(h => h.replace(/^"|"$/g, '').trim());
          const data = [];

          for (let i = 1; i < lines.length; i++) {
            if (lines[i].trim()) {
              const values = parseCSVLine(lines[i], delimiter).map(v => v.replace(/^"|"$/g, '').trim());
              const row: any = {};

              headers.forEach((header, index) => {
                row[header] = values[index] || '';
              });

              data.push(row);
            }
          }

          resolve(data);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  private async parseExcel(file: File): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          // Get headers from first row
          const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          if (!json.length || json.length < 2) {
            reject(new Error('Excel file must have a header row and at least one data row.'));
            return;
          }

          const headerRow: string[] = json[0] as string[];
          // Convert data rows to objects with header names as keys
          const dataRows = XLSX.utils.sheet_to_json(worksheet, { header: headerRow, range: 1 });

          resolve(dataRows as any[]);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  }

  private async processImportedData(data: any[]): Promise<void> {
    if (!this.selectedDataset) return;

    // Map the imported data to match the dataset schema
    const mappedData = data.map(row => {
      const mappedRow: any = {};

      this.selectedDataset!.dataTableSchemas.forEach(schema => {
        const fieldName = schema.fieldName;
        // Try to match by field name first, then by display name
        const value = row[fieldName] || row[schema.displayName] || '';

        // Convert data types as needed
        if (schema.dataType === DataSchemaDataType.DateTime && value) {
          // Handle different date formats
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            // Format as local ISO string (YYYY-MM-DDTHH:mm:ss)
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            mappedRow[fieldName] = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
          } else {
            mappedRow[fieldName] = value;
          }
        } else if (schema.dataType === DataSchemaDataType.Integer && value) {
          mappedRow[fieldName] = parseInt(value) || 0;
        } else if (schema.dataType === DataSchemaDataType.Decimal && value) {
          mappedRow[fieldName] = parseFloat(value) || 0;
        } else if (schema.dataType === DataSchemaDataType.Boolean && value) {
          // Handle boolean values
          if (typeof value === 'boolean') {
            mappedRow[fieldName] = value;
          } else if (typeof value === 'string') {
            mappedRow[fieldName] = value.toLowerCase() === 'true' || value === '1';
          } else {
            mappedRow[fieldName] = value;
          }
        } else {
          mappedRow[fieldName] = value;
        }
      });

      return mappedRow;
    });

    // Submit the imported data
    await this.submitImportedData(mappedData);
  }

  private async submitImportedData(data: any[]): Promise<void> {
    if (!this.selectedDataset) return;

    return new Promise((resolve, reject) => {
      this.dynamicTableService.addEntries(this.selectedDataset!.datasetReferenceTable.datasetId, data)
        .subscribe({
          next: (success) => {
            if (success) {
              resolve();
            } else {
              reject(new Error('Failed to import data'));
            }
          },
          error: (error) => {
            console.error('Error importing data:', error);
            reject(error);
          }
        });
    });
  }

  // Filter methods
  addFilterRow(): void {
    this.filterRows.push({
      fieldId: null,
      fieldName: '',
      value: '',
      value2: '',
      dateValue: null,
      dateValue2: null,
      matchMode: 'exact',
      dataType: '',
      availableMatchModes: []
    });
  }

  removeFilterRow(index: number): void {
    this.filterRows.splice(index, 1);
  }

  getAvailableFilterColumns(): any[] {
    // Return all columns that are not hidden
    return this.columns.filter(col => !col.isHiddenInternal);
  }

  onFilterFieldChange(filterRow: any): void {
    // When a field is selected, store its fieldId and dataType for backend filtering
    const selectedColumn = this.columns.find(col => col.field === filterRow.fieldName);
    if (selectedColumn) {
      const schema = this.selectedDataset?.dataTableSchemas.find(s => s.fieldName === selectedColumn.field);
      filterRow.fieldId = schema?.fieldId || null;
      filterRow.dataType = schema?.dataType || DataSchemaDataType.String;

      // Cache available match modes for this data type
      filterRow.availableMatchModes = this.getAvailableMatchModesForField(filterRow.dataType);

      // Set default match mode based on data type
      if (this.isNumericOrDateType(filterRow.dataType)) {
        filterRow.matchMode = 'exact';
      } else {
        filterRow.matchMode = 'partial';  // Default to partial for strings for better UX
      }

      // Clear values when field changes
      filterRow.value = '';
      filterRow.value2 = '';
      filterRow.dateValue = null;
      filterRow.dateValue2 = null;
    }
  }

  getAvailableMatchModesForField(dataType: string): { label: string, value: MatchMode }[] {
    const stringModes = [
      { label: 'Starts with', value: 'starts_with' as MatchMode },
      { label: 'Contains (partial match)', value: 'partial' as MatchMode },
      { label: 'Equals (exact match)', value: 'exact' as MatchMode },
      { label: 'Not Equal', value: 'not_equal' as MatchMode }
    ];

    const numericModes = [
      { label: 'Equals', value: 'exact' as MatchMode },
      { label: 'Not Equal', value: 'not_equal' as MatchMode },
      { label: 'Greater than', value: 'greater_than' as MatchMode },
      { label: 'Greater than or equal to', value: 'greater_than_or_equal' as MatchMode },
      { label: 'Less than', value: 'less_than' as MatchMode },
      { label: 'Less than or equal to', value: 'less_than_or_equal' as MatchMode },
      { label: 'Between', value: 'between' as MatchMode }
    ];

    if (this.isNumericOrDateType(dataType)) {
      return numericModes;
    } else {
      return stringModes;
    }
  }

  onFilterDateChange(filterRow: any, isSecondValue: boolean = false): void {
    // Convert date to ISO string format for the filter value
    const date = isSecondValue ? filterRow.dateValue2 : filterRow.dateValue;
    if (date) {
      const formatted = this.formatDateForFilter(date);
      if (isSecondValue) {
        filterRow.value2 = formatted;
      } else {
        filterRow.value = formatted;
      }
    } else {
      if (isSecondValue) {
        filterRow.value2 = '';
      } else {
        filterRow.value = '';
      }
    }
  }

  formatDateForFilter(date: Date): string {
    // Format date as ISO string without time for filtering
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00`;
  }

  isNumericOrDateType(dataType: string): boolean {
    const numericTypes = [DataSchemaDataType.Integer, DataSchemaDataType.Decimal, DataSchemaDataType.DateTime];
    return numericTypes.includes(dataType as DataSchemaDataType);
  }

  isNumericType(dataType: string): boolean {
    const numericTypes = [DataSchemaDataType.Integer, DataSchemaDataType.Decimal];
    return numericTypes.includes(dataType as DataSchemaDataType);
  }

  isDateType(dataType: string): boolean {
    return dataType === DataSchemaDataType.DateTime;
  }

  validateFilterMatchMode(filterRow: any): string | null {
    if (!filterRow.dataType || !filterRow.matchMode) {
      return null;
    }

    const isNumeric = this.isNumericOrDateType(filterRow.dataType);
    const isString = !isNumeric;

    // Validate partial match only works on strings
    if (filterRow.matchMode === 'partial' && isNumeric) {
      return 'Partial match is only supported for text fields';
    }

    // Validate greater_than, less_than, between only work on numbers/dates
    const numericOnlyModes: MatchMode[] = ['greater_than', 'less_than', 'between'];
    if (numericOnlyModes.includes(filterRow.matchMode) && isString) {
      return 'This match mode is only supported for numeric and date fields';
    }

    // Validate 'between' requires two values
    if (filterRow.matchMode === 'between') {
      if (!filterRow.value || !filterRow.value2) {
        return 'Between mode requires two values';
      }
    }

    return null;
  }

  onMatchModeChange(filterRow: any): void {
    // Clear value2 and dateValue2 if not in 'between' mode
    if (filterRow.matchMode !== 'between') {
      filterRow.value2 = '';
      filterRow.dateValue2 = null;
    }
  }

  applyFilters(): void {
    if (!this.selectedDataset) return;

    // Build the filters object from filterRows using the new format
    this.activeFilters = {};
    const validationErrors: string[] = [];

    this.filterRows.forEach((filterRow, index) => {
      // Validate the filter
      const validationError = this.validateFilterMatchMode(filterRow);
      if (validationError) {
        validationErrors.push(`Filter ${index + 1}: ${validationError}`);
        return;
      }

      if (filterRow.fieldId && filterRow.value && filterRow.value.trim() !== '') {
        // Build values array
        let values: (string | number)[] = [];

        if (filterRow.matchMode === 'between') {
          // For 'between', we need two values
          if (filterRow.value2 && filterRow.value2.trim() !== '') {
            // Convert to appropriate type based on data type
            if (this.isNumericType(filterRow.dataType)) {
              values = [parseFloat(filterRow.value), parseFloat(filterRow.value2)];
            } else if (this.isDateType(filterRow.dataType)) {
              // DateTime values are already formatted as ISO strings
              values = [filterRow.value.trim(), filterRow.value2.trim()];
            } else {
              values = [filterRow.value.trim(), filterRow.value2.trim()];
            }
          } else {
            validationErrors.push(`Filter ${index + 1}: Between mode requires two values`);
            return;
          }
        } else {
          // For other modes, single value
          if (this.isNumericType(filterRow.dataType)) {
            values = [parseFloat(filterRow.value)];
          } else if (this.isDateType(filterRow.dataType)) {
            // DateTime values are already formatted as ISO strings
            values = [filterRow.value.trim()];
          } else {
            values = [filterRow.value.trim()];
          }
        }

        // Build the new filter format
        this.activeFilters[filterRow.fieldId] = {
          values: values,
          matchMode: filterRow.matchMode
        };
      }
    });

    // Show validation errors if any
    if (validationErrors.length > 0) {
      alert('Filter validation errors:\n\n' + validationErrors.join('\n'));
      return;
    }

    this.hasActiveFilters = Object.keys(this.activeFilters).length > 0;

    // Reset to first page and reload data with filters
    this.first = 0;
    this.refreshData();
  }

  clearFilters(): void {
    this.resetFiltersState();
    this.refreshData();
  }

  // Clears filter state without triggering a data reload
  // Use this when switching datasets to avoid duplicate API calls
  resetFiltersState(): void {
    this.filterRows = [];
    this.activeFilters = {};
    this.hasActiveFilters = false;
    this.first = 0;
  }

  // ---------------------------------------------------------------------------
  // Column selection and grouping
  // ---------------------------------------------------------------------------

  /**
   * Columns currently rendered in the data table: the aggregated result when a group-by
   * is applied, otherwise the columns the user kept selected.
   */
  get displayColumns(): any[] {
    if (this.isGroupedView) {
      return this.groupedColumns;
    }

    // The internal key column isn't offered for selection, so it keeps being shown the
    // way it always was rather than disappearing the first time the picker is touched.
    return this.columns.filter(col => col.isHiddenInternal || this.selectedColumnFields.includes(col.field));
  }

  /**
   * Columns offered for selecting, grouping and aggregating - the internal key is an
   * implementation detail of the table, not something to group or report on.
   */
  getSelectableColumns(): any[] {
    return this.columns.filter(col => !col.isHiddenInternal);
  }

  /**
   * Columns the group-by and aggregation pickers offer: whatever is currently selected.
   */
  getGroupableColumns(): any[] {
    return this.getSelectableColumns().filter(col => this.selectedColumnFields.includes(col.field));
  }

  // Grouped rows can't be edited or added to - they aren't rows of the table
  get canEditData(): boolean {
    return !this.isViewDataset && !this.isGroupedView;
  }

  private resetColumnAndGroupState(): void {
    this.selectedColumnFields = this.getSelectableColumns().map(col => col.field);
    this.lastAppliedColumnSelection = [...this.selectedColumnFields].sort().join('|');
    this.groupByFields = [];
    this.aggregationRows = [];
    this.isGroupedView = false;
    this.groupedColumns = [];
    this.activeGroupBy = [];
    this.activeAggregations = [];
  }

  /**
   * Field ids to request. Undefined means every column (SELECT *), which is both the
   * default and what the server does with an empty list.
   *
   * The primary key travels with every request even when its column is deselected -
   * editing an existing row needs it to identify the row being updated.
   */
  private getRequestedFieldIds(): number[] | undefined {
    if (this.selectedColumnFields.length >= this.getSelectableColumns().length) {
      return undefined;
    }

    const fieldIds = this.columns
      .filter(col => this.selectedColumnFields.includes(col.field) || col.isPrimaryKey || col.isHiddenInternal)
      .map(col => col.fieldId)
      .filter(fieldId => !!fieldId);

    return fieldIds.length > 0 ? fieldIds : undefined;
  }

  onSelectedColumnsChange(): void {
    // The picker reports every close, including the ones that changed nothing
    const selection = [...this.selectedColumnFields].sort().join('|');
    if (selection === this.lastAppliedColumnSelection) {
      return;
    }
    this.lastAppliedColumnSelection = selection;

    // An empty selection would leave nothing to show, so treat it as "all columns"
    if (this.selectedColumnFields.length === 0) {
      this.selectAllColumns();
      return;
    }

    // Drop grouping that refers to columns that are no longer selected
    this.groupByFields = this.groupByFields.filter(field => this.selectedColumnFields.includes(field));
    this.aggregationRows = this.aggregationRows.filter(row =>
      !row.fieldName || row.fieldName === this.ROW_COUNT_FIELD || this.selectedColumnFields.includes(row.fieldName));

    this.first = 0;

    if (this.isGroupedView) {
      // Re-apply so the grouped result matches the trimmed configuration
      this.applyGroupBy();
      return;
    }

    this.refreshData();
  }

  selectAllColumns(): void {
    this.selectedColumnFields = this.getSelectableColumns().map(col => col.field);
    this.lastAppliedColumnSelection = [...this.selectedColumnFields].sort().join('|');
    this.first = 0;

    if (this.isGroupedView) {
      this.applyGroupBy();
      return;
    }

    this.refreshData();
  }

  addAggregationRow(): void {
    this.aggregationRows.push({ fieldName: '', func: 'sum' });
  }

  removeAggregationRow(index: number): void {
    this.aggregationRows.splice(index, 1);
  }

  /**
   * Functions offered for a column: SUM and AVG are dropped for anything the database
   * can't add up.
   */
  getAggregationFunctionsFor(fieldName: string): { value: AggregationFunction, label: string }[] {
    if (!fieldName) {
      return this.aggregationFunctions;
    }

    if (fieldName === this.ROW_COUNT_FIELD) {
      return this.aggregationFunctions.filter(fn => fn.value === 'count');
    }

    const column = this.columns.find(col => col.field === fieldName);
    const isNumeric = column ? this.isNumericType(column.dataType) : false;

    return this.aggregationFunctions.filter(fn => isNumeric || !fn.numericOnly);
  }

  onAggregationFieldChange(row: { fieldName: string, func: AggregationFunction }): void {
    if (row.fieldName === this.ROW_COUNT_FIELD) {
      row.func = 'count';
      return;
    }

    // Keep the row valid when switching to a column the current function can't be used on
    const available = this.getAggregationFunctionsFor(row.fieldName);
    if (!available.some(fn => fn.value === row.func)) {
      row.func = available[0]?.value ?? 'count';
    }
  }

  getAggregationLabel(func: AggregationFunction): string {
    return this.aggregationFunctions.find(fn => fn.value === func)?.label ?? func;
  }

  /**
   * Turns the group-by draft into a request: validates it, builds the columns the
   * grouped result will be rendered with, and reloads.
   */
  applyGroupBy(): void {
    if (!this.selectedDataset) return;

    // Nothing to group by is the ungrouped view
    if (this.groupByFields.length === 0) {
      this.clearGroupBy();
      return;
    }

    const errors: string[] = [];
    const groupBy: number[] = [];
    const aggregations: AggregationCriteria[] = [];
    const groupedColumns: any[] = [];

    this.groupByFields.forEach(fieldName => {
      const column = this.columns.find(col => col.field === fieldName);
      if (!column?.fieldId) {
        errors.push(`Group by: '${fieldName}' is not a field of this dataset`);
        return;
      }

      groupBy.push(column.fieldId);
      groupedColumns.push(column);
    });

    const seen = new Set<string>();
    this.aggregationRows.forEach((row, index) => {
      if (!row.fieldName || !row.func) {
        errors.push(`Aggregation ${index + 1}: pick a column and a function`);
        return;
      }

      const key = `${row.fieldName}:${row.func}`;
      if (seen.has(key)) {
        errors.push(`Aggregation ${index + 1}: this column and function are already aggregated`);
        return;
      }
      seen.add(key);

      if (row.fieldName === this.ROW_COUNT_FIELD) {
        // fieldId 0 with count is the API's row count per group
        aggregations.push({ fieldId: 0, function: 'count' });
        groupedColumns.push(this.buildAggregateColumn('Row Count', ROW_COUNT_ALIAS));
        return;
      }

      const column = this.columns.find(col => col.field === row.fieldName);
      if (!column?.fieldId) {
        errors.push(`Aggregation ${index + 1}: '${row.fieldName}' is not a field of this dataset`);
        return;
      }

      if (this.groupByFields.includes(row.fieldName)) {
        errors.push(`Aggregation ${index + 1}: '${column.header}' is already a group-by column`);
        return;
      }

      const isNumericOnly = this.aggregationFunctions.find(fn => fn.value === row.func)?.numericOnly;
      if (isNumericOnly && !this.isNumericType(column.dataType)) {
        errors.push(`Aggregation ${index + 1}: ${this.getAggregationLabel(row.func)} only works on numeric columns`);
        return;
      }

      aggregations.push({ fieldId: column.fieldId, function: row.func });
      groupedColumns.push(this.buildAggregateColumn(
        `${this.getAggregationLabel(row.func)} of ${column.header}`,
        aggregationAlias(column.field, row.func)));
    });

    if (errors.length > 0) {
      alert('Group by validation errors:\n\n' + errors.join('\n'));
      return;
    }

    this.activeGroupBy = groupBy;
    this.activeAggregations = aggregations;
    this.groupedColumns = groupedColumns;
    this.isGroupedView = true;

    // A grouped result has no rows to edit
    this.isEditing = false;
    this.newRows = [];

    this.first = 0;
    this.refreshData();
  }

  clearGroupBy(): void {
    const wasGrouped = this.isGroupedView;

    this.groupByFields = [];
    this.aggregationRows = [];
    this.isGroupedView = false;
    this.groupedColumns = [];
    this.activeGroupBy = [];
    this.activeAggregations = [];

    if (wasGrouped) {
      this.first = 0;
      this.refreshData();
    }
  }

  /**
   * Column definition for an aggregate. The aggregated value arrives under the alias the
   * API assigned it, and it is never a reference or an editable field.
   */
  private buildAggregateColumn(header: string, alias: string): any {
    return {
      field: alias,
      header: header,
      fieldId: null,
      sortable: true,
      referenceId: 0,
      dataType: DataSchemaDataType.Decimal,
      isPrimaryKey: false,
      isHiddenInternal: false,
      isFileStorageReference: false
    };
  }
}
